import React, { Fragment, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get } from 'lodash-es';
import Button from 'reactstrap/lib/Button';
import { useToggle } from 'site-modules/shared/hooks/use-toggle';
import { FirstContentEntity } from 'client/data/models/editorial-review';
import { VehicleEntities } from 'client/data/models/vehicle-v2';
import { VisitorEntities } from 'client/data/models/visitor';
import { COLLAPSE_STATE, THANK_YOU_VIEWS } from 'site-modules/shared/constants/lead-form/lead-form-params';
import { LEAD_FORM_CONFIGS } from 'site-modules/shared/constants/lead-form/lead-form-configs';
import { getLeadSubmissionInfoWithoutEmail } from 'site-modules/shared/components/lead-form/utils/utils';
import { getLeadFormCreativeId } from 'site-modules/shared/components/lead-form/utils/lead-form-creative-id';
import { TransparentPricingLeadFormDrawer } from 'site-modules/shared/components/lead-form/transparent-pricing-lead-form/transparent-pricing-lead-form-drawer';
import { FIND_BEST_PRICE_FORM_NAME } from 'site-modules/personalized-search/constants/tracking';

const LEAD_FORM_COPY = 'Contact your local dealers about upcoming availability and pricing details.';

export function PreprodPricingButton({
  firstContent = null,
  vehicle,
  mostPopularStyleId = null,
  visitorLocation = null,
  isMobile = false,
  leadFormDealerCount = null,
  className = null,
  btnClassName = null,
  pageName = '',
  ctaText,
  creativeId = undefined,
  getLeadFormSrpLinkAttributes = undefined,
  onDrawerHide = undefined,
}) {
  const {
    make: { name: makeName },
    model: { name: modelName },
    year,
  } = vehicle;
  const [isDrawerOpened, toggleDrawerOpened] = useToggle(false);

  const getLeadSubmissionInfoWithComments = useCallback(
    params => {
      const submissionInfo = getLeadSubmissionInfoWithoutEmail(params);

      return {
        ...submissionInfo,
        leads: submissionInfo.leads.map(lead => ({
          ...lead,
          comments: `I'm interested in the ${year} ${makeName} ${modelName} once it becomes available.`,
        })),
      };
    },
    [makeName, modelName, year]
  );

  if (leadFormDealerCount === null) {
    return null;
  }

  const dataTracking = getLeadFormCreativeId({
    isNewVin: true,
    pageNameLegacy: pageName,
    formName: FIND_BEST_PRICE_FORM_NAME,
  });

  return (
    <Fragment>
      {!!leadFormDealerCount && (
        <div className={classnames('preprod-pricing-button', className)}>
          <Button
            color="blue-50"
            size="lg"
            className={classnames('pricing-btn text-white text-transform-none fw-medium mt-1_5 mb-0_5', btnClassName)}
            onClick={toggleDrawerOpened}
            data-tracking-id="view_pricing_details"
            data-hidedelightedoninteraction
            data-tracking-parent={creativeId}
          >
            {ctaText}
          </Button>
        </div>
      )}

      <TransparentPricingLeadFormDrawer
        isOpen={isDrawerOpened}
        onDrawerToggle={toggleDrawerOpened}
        params={{ styleId: mostPopularStyleId, vehicle, zipCode: visitorLocation?.zipCode }}
        options={{
          isMobile,
          creativeId: dataTracking,
          headingText: `${vehicle.year} ${vehicle.make.name} ${vehicle.model.name}`,
          explainingTextDesktop: LEAD_FORM_COPY,
          explainingTextMobile: LEAD_FORM_COPY,
          thankYouComponent: THANK_YOU_VIEWS.TRANSPARENT_PRICING_PREPROD,
          thankYouHeadingText: 'Success! You’re all set.',
          thankYouExplainingText: null,
          thankYouPriceValue: get(firstContent, 'price.value', {}),
          processDataMethods: { getLeadSubmissionInfo: getLeadSubmissionInfoWithComments },
          initialCollapseState: COLLAPSE_STATE.PARTIAL_COLLAPSE,
          leadFormComponentConfig: LEAD_FORM_CONFIGS.QUICK_QUOTES_ROI,
        }}
        creativeId={dataTracking}
        getLeadFormSrpLinkAttributes={getLeadFormSrpLinkAttributes}
        onDrawerHide={onDrawerHide}
      />
    </Fragment>
  );
}

PreprodPricingButton.propTypes = {
  vehicle: VehicleEntities.MakeModelSubmodelYear.isRequired,
  ctaText: PropTypes.string.isRequired,
  firstContent: FirstContentEntity,
  mostPopularStyleId: PropTypes.number,
  leadFormDealerCount: PropTypes.number,
  className: PropTypes.string,
  btnClassName: PropTypes.string,
  visitorLocation: VisitorEntities.Location,
  isMobile: PropTypes.bool,
  pageName: PropTypes.string,
  creativeId: PropTypes.string,
  getLeadFormSrpLinkAttributes: PropTypes.func,
  onDrawerHide: PropTypes.func,
};
